<template>
  <custom-callout class="mb-3" :has-toggle="false">
    <div class="row py-3 mr-md-0 n-ml">
      <div class="col-md-2 col-12 text-center border-right">
        <div
          class="badge badge-pill font-16 py-2 my-2 w-100"
          :style="{
            'background-color': areaColor.bgColor,
            color: areaColor.color,
          }"
        >
          {{ data.area?.name }}
        </div>
        <span class="color-gray-6 d-block font-14 pt-1">
          No. de Rúbrica:
        </span>
        <span class="font-weight-bold">
          {{ data.number }}
        </span>
      </div>
      <div class="col-md col-12 pl-4 pr-0">
        <div class="row">
          <div class="col-12 col-md-3">
            <div class="mb-2">
              <span class="color-gray-6 d-block font-14">
                Servicio a Ofrecer:
              </span>
              <span class="font-16 color-black ">
                {{ data.service?.name | empty }}
              </span>
            </div>
          </div>
          <div class="col-12 col-md-6">
            <div class="mb-2">
              <span class="color-gray-6 d-block font-14">
                Tipo de Evaluación:
              </span>
              <span class="font-16 color-black ">
                {{ data.type?.name | empty }}
              </span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md">
            <div>
              <span class="color-gray-6 d-block font-14">
                Estatus:
              </span>
              <div class="d-flex align-items-center">
                <em
                  class="fas fa-circle mr-2 font-12"
                  :style="{ color: statusColor }"
                />
                <span class="font-16 color-black font-weight-bold">
                  {{ data.status?.name | empty }}
                </span>
              </div>
            </div>
          </div>
          <div class="col-12 col-md">
            <div>
              <span class="color-gray-6 d-block font-14">
                Año Académico:
              </span>
              <span class="font-16 color-black ">
                {{ data.academicYear?.name | empty }}
              </span>
            </div>
          </div>
          <div class="col-12 col-md">
            <div>
              <span class="color-gray-6 d-block font-14">
                Fecha de Creación:
              </span>
              <span class="font-16 color-black ">
                {{ data.date | date }}
              </span>
            </div>
          </div>
          <div class="col-12 col-md">
            <div>
              <span class="color-gray-6 d-block font-14">
                Fecha de Publicación:
              </span>
              <span class="font-16 color-black ">
                {{ data.publicationDate | date }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="actionItems.length"
        class="col-md-1 col-12 pr-5 d-flex align-items-center justify-content-center"
      >
        <dropdown
          always-show-dropdown
          class="actions-dropdown"
          :items="actionItems"
          title="Acciones"
        />
      </div>
    </div>
  </custom-callout>
</template>

<script>
//libs components
import CustomCallout from 'custom-callout';
import Dropdown from 'dropdown';

import { remove, rollbackToDraft } from '@/modules/rubric/api.js';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import ProposalArea from '@/utils/shared/constants/proposalArea.js';
import RubricStatus from '@/utils/shared/constants/rubricStatus.js';

export default {
  name: 'ResultRow',
  components: {
    CustomCallout,
    Dropdown,
  },
  mixins: [alertMixin],
  props: {
    data: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    rubricId() {
      const { id } = this.data;
      return Number(id);
    },
    isSubmitted() {
      return this.data.status.id === RubricStatus.Submitted;
    },
    isPublished() {
      return this.data.status.id === RubricStatus.Published;
    },
    isDraft() {
      return this.data.status.id === RubricStatus.Draft;
    },
    canRemove() {
      return (
        this.userHasPermissions('provider.rubricsettings.delete') &&
        this.isDraft
      );
    },
    canRollbackDraft() {
      return (
        this.userHasPermissions('provider.rubricsettings.rollback.draft') &&
        this.isSubmitted
      );
    },
    areaColor() {
      switch (this.data.area.id) {
        case ProposalArea.SpecialEducation:
          return { bgColor: '#FED8BC', color: '#D44D28' };
        case ProposalArea.ProvisionalRemedy:
          return { bgColor: '#FDDCF1', color: '#9A246D' };
        case ProposalArea.Transportation:
          return { bgColor: '#B4E9EB', color: '#1E8C9A' };
        default:
          return { bgColor: '#B5C3BC', color: '#606E70' };
      }
    },
    statusColor() {
      switch (this.data.status.id) {
        case RubricStatus.Draft:
          return '#B5C3BC';
        case RubricStatus.Submitted:
          return '#FAE76F';
        case RubricStatus.Published:
          return '#A3E5B3';
        default:
          return '#606E70';
      }
    },
    actionItems() {
      return [
        {
          label: 'Continuar Edición',
          icon: 'fas fa-edit text-green',
          visible: this.isDraft,
          click: () => {
            this.$router.push({
              name: 'RubricEdit',
              params: { id: this.rubricId },
            });
          },
        },
        {
          label: 'Regresar a Borrador',
          icon: 'fas fa-reply-all text-green',
          visible: this.canRollbackDraft,
          click: this.rollbackToDraft,
        },
        {
          label: 'Vista Preliminar',
          icon: 'fas fa-eye text-green',
          visible: this.isSubmitted || this.isPublished,
          click: () => {
            this.$router.push({
              name: 'RubricPreview',
              params: { id: this.rubricId },
            });
          },
        },
        {
          label: 'Eliminar',
          icon: 'fas fa-times-circle text-danger',
          visible: this.canRemove,
          click: this.erase,
        },
      ];
    },
  },
  methods: {
    async erase() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea eliminar la configuración?',
        'Sí',
        'No'
      );

      if (!confirmed) return;

      await remove(this.rubricId).catch((e) =>
        this.ShowToast('', e?.response?.data?.message, 'error')
      );
      this.$emit('refresh');
    },
    async rollbackToDraft() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea regresar la rúbrica a borrador?',
        'Sí',
        'No'
      );

      if (!confirmed) return;

      await rollbackToDraft(this.rubricId)
        .then(() => {
          this.ShowToast(
            'Proceso exitoso',
            'Los cambios fueron guardados exitosamente',
            'success'
          );
        })
        .catch((e) => this.ShowToast('', e?.response?.data?.message, 'error'));
      this.$emit('refresh');
    },
  },
};
</script>
<style lang="scss" scoped>
.n-ml {
  margin-left: -1.5rem !important;
}

.custom-w-100,
.custom-w-recommended,
.custom-w-not-recommended,
.custom-w-submitted {
  width: 100%;
}

.custom-pl {
  padding-left: 15px;
}

@media (max-width: 1260px) {
  .custom-w-submitted {
    width: 108px;
  }
  .custom-w-recommended {
    width: 130px;
  }
  .custom-w-not-recommended {
    width: 150px;
  }
  .col-md-2 {
    padding-left: 0px !important;
  }
  .custom-pl {
    padding-left: 0px;
  }
}

@media (max-width: 767px) {
  .custom-w-recommended,
  .custom-w-not-recommended,
  .custom-w-submitted {
    width: 100%;
  }

  .actions-dropdown {
    ::v-deep {
      .dropdown-toggle {
        width: 100%;
        font-size: 0.875rem;
      }
    }
  }
}
</style>
